import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ALERT_CATEGORIES, SEVERITY_STYLES, severityFor } from "@/components/vendor/alerts";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDaysUntil = (days) => {
  if (days === null || days === undefined) return "";
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  if (days === 0) return "due today";
  return `in ${days} day${days === 1 ? "" : "s"}`;
};

// Compact icon-pill row shown on each vendor car card. Each pill is colored by severity
// (rose = expired, orange = critical < 7d, amber = warning < 30d, emerald = ok, neutral = unset).
// Hover/tap a pill → Popover with the date, days-until, and the vendor's free-text note.
export default function CarAlertsPills({ car }) {
  const alerts = car?.alerts || {};
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`car-alerts-pills-${car.id}`}>
      {ALERT_CATEGORIES.map(({ key, dateField, noteField, label, icon: Icon }) => {
        const date = alerts[dateField];
        const { severity, daysUntil } = severityFor(date);
        const s = SEVERITY_STYLES[severity];
        // Suppress emerald "OK" + neutral "unset" pills entirely — show only items that matter.
        // Vendors who want to inspect everything can open the edit-car dialog.
        if (severity === "ok" || severity === "none") return null;
        return (
          <Popover key={key}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border} hover:brightness-105 transition`}
                data-testid={`car-alert-${car.id}-${key}`}
                aria-label={`${label} alert: ${s.label}`}
              >
                <Icon className="h-2.5 w-2.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 text-xs" align="start" data-testid={`car-alert-popover-${car.id}-${key}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="h-3.5 w-3.5 text-neutral-600" />
                <span className="font-semibold text-neutral-900">{label}</span>
                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full ${s.bg} ${s.text}`}>{s.label}</span>
              </div>
              <div className="space-y-0.5">
                <div className="text-neutral-500">Expires</div>
                <div className="font-medium text-neutral-900">{formatDate(date)} <span className="text-neutral-500 font-normal">· {formatDaysUntil(daysUntil)}</span></div>
              </div>
              {(alerts[noteField] || "").trim() && (
                <div className="mt-2 pt-2 border-t border-border space-y-0.5">
                  <div className="text-neutral-500">Note</div>
                  <div className="text-neutral-700 whitespace-pre-wrap">{alerts[noteField]}</div>
                </div>
              )}
            </PopoverContent>
          </Popover>
        );
      })}
    </div>
  );
}
